import React from 'react'
import PropTypes from 'prop-types'
import { useSelector, useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'

import { Button, ButtonType, Tooltip } from '@junglescout/edna'

import { showAccountSyncModal } from 'actions/account/sellerAccountSync'
import { useApiSubscription } from 'hooks/apiSubscription'
import { usePurchaseModal } from 'hooks/usePurchaseModal'

import { AMAZON_SELLER_KEYS_SYNC_STEPS } from 'constants/account'
import { Features } from 'constants/feature_names'

import { getPricingPageUrl } from 'helpers/account'
import { useFeatureFlag } from 'hooks/feature_flag'
import { PurchaseModal } from '../account/Subscriptions/Modal/PurchaseModal'

export const ConnectToAmazonButton = ({
  btnType = ButtonType.PRIMARY,
  btnText,
  className = '',
  dataId,
  iconName,
  isLoading,
  margin,
  onClick = () => {},
  width,
  mini
}) => {
  const { t } = useTranslation(['generic', 'account'])
  const {
    membershipInfo: { hasActiveMembership, group },
    user: {
      checkout_locale,
      currency,
      selected_language,
      is_account_owner: isAccountOwner
    }
  } = useSelector(state => state.globalData)

  const {
    showPurchaseModal,
    setShowPurchaseModal,
    openPurchaseModal,
    isChinaUserWithoutPlan,
    purchasePlans,
    isPurchasePlansLoading
  } = usePurchaseModal()

  const { sellerDataAvailable } = useSelector(
    state => state.amazonSellerAccounts
  )
  const { isEnterpriseApiTier } = useApiSubscription()

  const isSellerFeaturesMaintenance = useFeatureFlag(
    Features.SELLER_FEATURES_MAINTENANCE
  )

  const isSellerFeaturesDisabled =
    !sellerDataAvailable || isSellerFeaturesMaintenance

  const dispatch = useDispatch()

  const defaultButtonText = hasActiveMembership
    ? t('generic:integrateAccount', 'Integrate your account')
    : t('generic:UpgradeNow', 'Purchase Now')

  const buttonText = btnText || defaultButtonText

  const noActiveMembershipDestination = getPricingPageUrl(
    checkout_locale,
    currency,
    selected_language,
    group
  )

  const defaultClick = () => {
    if (isChinaUserWithoutPlan) {
      openPurchaseModal()
      return
    }

    if (!hasActiveMembership) {
      return window.location.assign(noActiveMembershipDestination)
    }

    dispatch(showAccountSyncModal(AMAZON_SELLER_KEYS_SYNC_STEPS.accountSync))
  }

  if (isEnterpriseApiTier) {
    return undefined
  }

  return (
    <>
      <Tooltip
        contentClasses="medium right"
        size="medium"
        disabled={
          isSellerFeaturesDisabled
            ? false
            : isAccountOwner || !hasActiveMembership
        }
        content={
          isSellerFeaturesDisabled
            ? t(
                'account:AmazonSellerAccounts.sellerFeatureDisabled',
                'Adding an Amazon seller account is currently unavailable. Please try again later.'
              )
            : t(
                'account:AmazonSellerAccounts.tooltipAddNewDisabled',
                'Adding or editing Amazon accounts is limited to the primary account holder.'
              )
        }>
        <span>
          <Button
            btnType={btnType}
            className={className}
            dataId={dataId}
            iconName={iconName}
            isLoading={isLoading}
            margin={margin}
            width={width}
            mini={mini}
            disabled={
              isSellerFeaturesDisabled ||
              (!isAccountOwner && hasActiveMembership)
            }
            onClick={() => {
              const destination = hasActiveMembership
                ? 'modal'
                : noActiveMembershipDestination
              onClick({ buttonText, destination })
              defaultClick()
            }}>
            {buttonText}
          </Button>
        </span>
      </Tooltip>
      <PurchaseModal
        isVisible={showPurchaseModal}
        isLoading={isPurchasePlansLoading}
        plans={purchasePlans || {}}
        handleBackdropClick={() => setShowPurchaseModal(false)}
      />
    </>
  )
}

ConnectToAmazonButton.propTypes = {
  btnType: PropTypes.string,
  btnText: PropTypes.string,
  className: PropTypes.string,
  dataId: PropTypes.string,
  iconName: PropTypes.string,
  isLoading: PropTypes.bool,
  onClick: PropTypes.func,
  margin: PropTypes.string,
  width: PropTypes.string,
  mini: PropTypes.bool
}
